import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  X, Search, Loader2, User, UserPlus, UserMinus, 
  Lock, Users, Music, Mic2, Radio, Building2, Camera, 
  Headphones, Palette, Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL as API } from '../config/api';
import UserAvatar from './UserAvatar';
import { getProfileUrl, getDisplayHandle } from '../utils/profileUtils';
import { lock, unlock } from '../utils/scrollLock';



const ROLE_ICONS = {
  listener: User,
  artist: Music,
  podcaster: Mic2,
  streamer: Radio,
  studio: Building2,
  videographer: Camera,
  engineer: Headphones,
  graphic_designer: Palette,
  producer: Wrench,
  organization: Building2,
  video_creator: Camera
};

const ROLE_LABELS = {
  listener: 'Listener',
  artist: 'Artist',
  podcaster: 'Podcaster',
  streamer: 'Streamer',
  studio: 'Studio',
  videographer: 'Videographer',
  engineer: 'Engineer',
  graphic_designer: 'Designer',
  producer: 'Producer',
  organization: 'Organization',
  video_creator: 'Video Creator'
};

export default function FollowersModal({ 
  isOpen, 
  onClose, 
  userId, 
  type = 'followers', // 'followers' or 'following'
  userName = ''
}) {
  const { user: currentUser, isAuthenticated, getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [isPrivate, setIsPrivate] = useState(false);
  const [loadingFollow, setLoadingFollow] = useState({});

  const isOwnProfile = currentUser?.user_id === userId;
  const title = type === 'followers' ? 'Followers' : 'Following';

  // Manage scroll lock
  useEffect(() => {
    if (isOpen) {
      lock();
    }
    return () => {
      if (isOpen) {
        unlock();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUsers();
    }
  }, [isOpen, userId, type, searchQuery, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/users/${userId}/${type}?limit=50`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (roleFilter && roleFilter !== 'all') url += `&role=${roleFilter}`;

      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(url, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setUsers(data[type] || []);
        setTotal(data.total || 0);
        setIsPrivate(data.is_private || false);
      }
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (targetUserId, currentlyFollowing) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to follow users');
      return;
    }

    setLoadingFollow(prev => ({ ...prev, [targetUserId]: true }));
    try {
      const endpoint = currentlyFollowing ? 'unfollow' : 'follow';
      const res = await fetch(`${API}/api/users/${targetUserId}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        // Update local state
        setUsers(prev => prev.map(u => 
          u.user_id === targetUserId 
            ? { ...u, is_following: !currentlyFollowing }
            : u
        ));
        toast.success(currentlyFollowing ? 'Unfollowed' : 'Now following');
      }
    } catch (err) {
      toast.error('Failed to update follow status');
    } finally {
      setLoadingFollow(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  const handleRemoveFollower = async (followerId) => {
    if (!confirm('Remove this follower? They can still follow you again.')) return;

    try {
      const res = await fetch(`${API}/api/users/${userId}/followers/${followerId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setUsers(prev => prev.filter(u => u.user_id !== followerId));
        setTotal(prev => prev - 1);
        toast.success('Follower removed');
      }
    } catch (err) {
      toast.error('Failed to remove follower');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-background border border-border rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="border-b border-border p-4 flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-lg">{title}</h2>
              {userName && <p className="text-sm text-muted-foreground">@{userName}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Search and Filter */}
          <div className="p-4 border-b border-border space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/30 border-border"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="bg-muted/30 border-border">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="listener">Listeners</SelectItem>
                <SelectItem value="artist">Artists</SelectItem>
                <SelectItem value="podcaster">Podcasters</SelectItem>
                <SelectItem value="streamer">Streamers</SelectItem>
                <SelectItem value="producer">Producers</SelectItem>
                <SelectItem value="studio">Studios</SelectItem>
                <SelectItem value="organization">Organizations</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : isPrivate ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-bold mb-2">This list is private</h3>
                <p className="text-sm text-muted-foreground">
                  This user has chosen to keep their {type} list private.
                </p>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Users className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-bold mb-2">No {title.toLowerCase()}</h3>
                <p className="text-sm text-muted-foreground">
                  {searchQuery 
                    ? `No results for "${searchQuery}"`
                    : type === 'followers' 
                      ? "No one is following this account yet."
                      : "This account isn't following anyone yet."
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {users.map((user) => {
                  const RoleIcon = ROLE_ICONS[user.role] || User;
                  const isCurrentUser = currentUser?.user_id === user.user_id;
                  
                  return (
                    <div key={user.user_id} className="p-4 flex items-center gap-3 hover:bg-muted/30">
                      <Link 
                        to={getProfileUrl(user)} 
                        className="flex items-center gap-3 flex-1 min-w-0"
                        onClick={onClose}
                      >
                        <UserAvatar 
                          src={user.avatar_url || user.picture}
                          alt={user.name}
                          size="lg"
                          fallbackInitial={user.name}
                          updatedAt={user.avatar_updated_at}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{user.name}</div>
                          <div className="text-sm text-muted-foreground truncate">{getDisplayHandle(user)}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              <RoleIcon className="w-3 h-3 mr-1" />
                              {ROLE_LABELS[user.role] || user.role}
                            </Badge>
                            {user.city && (
                              <span className="text-xs text-muted-foreground">{user.city}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                      
                      {/* Action Buttons */}
                      {!isCurrentUser && isAuthenticated && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={user.is_following ? "outline" : "default"}
                            onClick={() => handleFollow(user.user_id, user.is_following)}
                            disabled={loadingFollow[user.user_id]}
                            className="min-w-[90px]"
                          >
                            {loadingFollow[user.user_id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : user.is_following ? (
                              <>
                                <UserMinus className="w-4 h-4 mr-1" />
                                Unfollow
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4 mr-1" />
                                Follow
                              </>
                            )}
                          </Button>
                          
                          {/* Remove follower button (only for own profile, followers list) */}
                          {isOwnProfile && type === 'followers' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveFollower(user.user_id)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with total */}
          {!isPrivate && total > 0 && (
            <div className="border-t border-border p-3 text-center text-sm text-muted-foreground">
              {total} {title.toLowerCase()}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
